// CLAD (Cross-Language Augmented Dictionary) and mBERT semantic evaluation simulation
// In real production, this integrates with mBERT transformer inference endpoints.

export interface EvaluationResult {
  score: number; // 0 - 10
  verdict: 'EXCELLENT' | 'GOOD' | 'PARTIAL' | 'NEEDS_IMPROVEMENT';
  matchedKeywords: string[];
  semanticSimilarity: number; // 0.0 - 1.0 (QWK / cosine)
  confidence: number; // 0.0 - 1.0
  needsHumanReview: boolean; // confidence < 0.65
  feedback_uz: string;
  feedback_ru: string;
}

// Morphological root extraction for Uzbek/Russian
export function extractStems(text: string, lang: 'uz' | 'ru' = 'ru'): string[] {
  const clean = text
    .toLowerCase()
    .replace(/[^a-zа-яёўқғҳ0-9\s]/gi, ' ')
    .split(/\s+/)
    .filter(Boolean);

  // Common suffix stripping for Uzbek
  const uzSuffixes = ['lar', 'ning', 'dan', 'ga', 'da', 'ni', 'siz', 'dagi', 'kor', 'chi', 'lash', 'ish'];
  
  return clean.map(word => {
    let stem = word;
    if (lang === 'uz') {
      for (const suf of uzSuffixes) {
        if (stem.length > suf.length + 3 && stem.endsWith(suf)) {
          stem = stem.slice(0, -suf.length);
          break;
        }
      }
    }
    return stem;
  });
}

// CLAD Synonym matching dictionary
const SYNONYM_DICTIONARY: Record<string, string[]> = {
  // Security
  'parol': ['parol', 'kod', 'kalit', 'password', 'pin'],
  'fishing': ['fishing', 'fosh', 'aldov', 'phishing', 'firibgarlik', 'soxta'],
  'xavfsizlik': ['xavfsizlik', 'himoya', 'himoyalash', 'security', 'kiberxavfsizlik'],
  'безопасность': ['безопасность', 'защита', 'security', 'кибербезопасность'],
  'фишинг': ['фишинг', 'мошенничество', 'спам', 'обман', 'подделка'],
  
  // Banking & Fintech
  'myid': ['myid', 'mayid', 'biometriya', 'identifikatsiya', 'id'],
  'e-imzo': ['e-imzo', 'imzo', 'elektron imzo', 'эцп', 'цифровая подпись'],
  'bank-klient': ['bank-klient', 'internet-banking', 'bank', 'hisob', 'клиент-банк'],
  'didox': ['didox', 'soliq', 'e-faktura', 'faktura', 'эдо'],
  'эдо': ['эдо', 'документооборот', 'счет-фактура', 'didox', 'фактура'],
  
  // AI
  'gallyutsinatsiya': ['gallyutsinatsiya', 'to\'qima', 'xato', 'галлюцинация', 'вымысел'],
  'prompt': ['prompt', 'so\'rov', 'buyruq', 'промпт', 'запрос', 'контекст'],
  'fakt-cheking': ['fakt-cheking', 'tekshirish', 'solishtirish', 'фактчекинг', 'верификация', 'проверка'],
  'konfidensial': ['konfidensial', 'maxfiy', 'sir', 'конфиденциальность', 'тайна']
};

export function evaluateFreeTextAnswer(
  answer: string,
  keywords: string[],
  sampleAnswer: string,
  lang: 'uz' | 'ru' = 'ru'
): EvaluationResult {
  if (!answer || answer.trim().length < 15) {
    return {
      score: 1,
      verdict: 'NEEDS_IMPROVEMENT',
      matchedKeywords: [],
      semanticSimilarity: 0.1,
      confidence: 0.95,
      needsHumanReview: false,
      feedback_uz: "Javob juda qisqa yoki mazmunsiz. Iltimos, batafsilroq va professional terminlarni qo'llagan holda yozing.",
      feedback_ru: "Ответ слишком краток или не содержит сути. Пожалуйста, дайте развернутый ответ с профессиональной аргументацией."
    };
  }

  const userStems = extractStems(answer, lang);
  const matched: string[] = [];

  keywords.forEach(kw => {
    const kwLower = kw.toLowerCase();
    const synonyms = SYNONYM_DICTIONARY[kwLower] || [kwLower];
    const isMatched = userStems.some(word => 
      synonyms.some(syn => word.includes(syn) || syn.includes(word))
    );
    if (isMatched) {
      matched.push(kw);
    }
  });

  const keywordCoverage = matched.length / Math.max(1, keywords.length);

  // Word count & structural depth penalty/bonus
  const wordCount = answer.trim().split(/\s+/).length;
  const lengthFactor = Math.min(1.0, wordCount / 40);

  // Estimated cosine similarity with sample expert answer (mBERT proxy)
  const sampleStems = extractStems(sampleAnswer, lang);
  let overlapCount = 0;
  userStems.forEach(stem => {
    if (sampleStems.some(s => s.includes(stem) || stem.includes(s))) {
      overlapCount++;
    }
  });
  const rawSimilarity = overlapCount / Math.max(10, sampleStems.length);
  const semanticSimilarity = Math.min(0.98, Math.max(0.15, (rawSimilarity * 0.5) + (keywordCoverage * 0.5)));

  // Final score 0-10
  const combined = (keywordCoverage * 0.55) + (semanticSimilarity * 0.3) + (lengthFactor * 0.15);
  const score = Math.round(combined * 10);

  let verdict: EvaluationResult['verdict'] = 'NEEDS_IMPROVEMENT';
  if (score >= 8) verdict = 'EXCELLENT';
  else if (score >= 6) verdict = 'GOOD';
  else if (score >= 4) verdict = 'PARTIAL';

  // Confidence estimation (uncertain middle responses might need expert review)
  const confidence = (score >= 8 || score <= 3) ? 0.92 : 0.74;
  const needsHumanReview = confidence < 0.65;

  return {
    score,
    verdict,
    matchedKeywords: matched,
    semanticSimilarity: Number(semanticSimilarity.toFixed(3)),
    confidence,
    needsHumanReview,
    feedback_uz: score >= 7 
      ? `A'lo! Siz amaliy vaziyatni to'g'ri tahlil qildingiz va kerakli raqamli mexanizmlarni (${matched.slice(0, 3).join(', ')}) to'liq qamrab oldingiz.`
      : `Javob qabul qilindi, ammo quyidagi jihatlarni to'ldirish tavsiya etiladi: ${keywords.filter(k => !matched.includes(k)).slice(0, 3).join(', ')}.`,
    feedback_ru: score >= 7
      ? `Отлично! Вы точно применили отраслевые регламенты и ключевые инструменты (${matched.slice(0, 3).join(', ')}). Ответ аргументирован.`
      : `Ответ зачтен частично. Рекомендуется глубже раскрыть аспекты: ${keywords.filter(k => !matched.includes(k)).slice(0, 3).join(', ')}.`
  };
}

